import { forwardRef, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import {
  ActionIcon,
  Autocomplete,
  Box,
  TextInput,
  Transition,
  type AutocompleteProps,
  type TextInputProps,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';

export interface SearchInputProps extends Omit<TextInputProps, 'onChange' | 'value' | 'defaultValue'> {
  value: string;
  onChange: (value: string) => void;
  /** Fires after `debounce` ms of inactivity — use for API queries */
  onDebouncedChange?: (value: string) => void;
  debounce?: number;
  /** Collapse to just the search icon until clicked/non-empty, expanding with an animation */
  collapsible?: boolean;
  /** Width while collapsed — just enough for the icon */
  collapsedWidth?: number;
  /** Width once expanded */
  expandedWidth?: number;
  /** Provide options to turn the plain filter input into a typeahead dropdown (Mantine Autocomplete) — omit for a plain free-text filter. */
  data?: AutocompleteProps['data'];
  /** Fires when a dropdown option is picked — look the full item up from `data` by this value, same contract as Mantine's `onOptionSubmit`. */
  onOptionSubmit?: (value: string) => void;
  renderOption?: AutocompleteProps['renderOption'];
  filter?: AutocompleteProps['filter'];
  maxDropdownHeight?: AutocompleteProps['maxDropdownHeight'];
  comboboxProps?: AutocompleteProps['comboboxProps'];
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onDebouncedChange,
      debounce = 300,
      collapsible = false,
      collapsedWidth = 30,
      expandedWidth = 240,
      data,
      onOptionSubmit,
      renderOption,
      filter,
      maxDropdownHeight,
      comboboxProps,
      style,
      onBlur,
      autoFocus,
      ...props
    },
    ref
  ) => {
    const callbackRef = useRef(onDebouncedChange);
    callbackRef.current = onDebouncedChange;
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        callbackRef.current?.(value);
      }, debounce);
      return () => clearTimeout(timer);
    }, [value, debounce]);

    const clearButton = value ? (
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        onClick={() => onChange('')}
        aria-label="Ryd søgning"
      >
        <IconX size={13} />
      </ActionIcon>
    ) : undefined;

    const renderInput = (
      inputStyle?: CSSProperties,
      inputAutoFocus?: boolean,
      inputOnBlur?: TextInputProps['onBlur']
    ) => {
      const shared = {
        ref,
        leftSection: <IconSearch size={15} />,
        rightSection: clearButton,
        value,
        autoFocus: inputAutoFocus ?? autoFocus,
        onBlur: inputOnBlur ?? onBlur,
        style: inputStyle ?? style,
        ...props,
      };

      if (data) {
        return (
          <Autocomplete
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- `shared`'s inferred type is TextInput's prop shape (its `...props` rest); Autocomplete accepts the same fields at runtime but its `vars` resolver type is structurally distinct
            {...(shared as any)}
            onChange={onChange}
            data={data}
            onOptionSubmit={onOptionSubmit}
            renderOption={renderOption}
            filter={filter}
            maxDropdownHeight={maxDropdownHeight}
            comboboxProps={comboboxProps}
          />
        );
      }

      return <TextInput {...shared} onChange={(e) => onChange(e.currentTarget.value)} />;
    };

    if (!collapsible) return renderInput();

    const isOpen = expanded || !!value;

    return (
      <Box
        pos="relative"
        h={30}
        style={{
          width: isOpen ? expandedWidth : collapsedWidth,
          flexShrink: 0,
          transition: 'width 150ms ease',
          ...style,
        }}
      >
        <Transition mounted={!isOpen} transition="fade" duration={120}>
          {(transitionStyle) => (
            <ActionIcon
              variant="default"
              c="dimmed"
              size={30}
              onClick={() => setExpanded(true)}
              aria-label={props.placeholder ?? 'Søg'}
              style={{ ...transitionStyle, position: 'absolute', inset: 0 }}
            >
              <IconSearch size={15} />
            </ActionIcon>
          )}
        </Transition>

        <Transition mounted={isOpen} transition="fade" duration={120}>
          {(transitionStyle) =>
            renderInput(
              { ...transitionStyle, position: 'absolute', inset: 0, width: '100%' },
              expanded,
              (e) => {
                onBlur?.(e);
                if (!value) setExpanded(false);
              }
            )
          }
        </Transition>
      </Box>
    );
  }
);

SearchInput.displayName = 'SearchInput';
